import { useCallback, useEffect, memo } from 'react'
import { ModalRenderer } from '@renderer/chat/components/modals/ModalRenderer'
import { useCloseActiveChat, DragAndDrop, useDragAndDrop } from '@renderer/chat'
import { Sidepanel } from './components/sidepanel'
import { SidepanelToolbarSection } from './components/sidepanel/sections/toolbar'
import { SidepanelSections } from './components/sidepanel/sidepanel-sections'
import { RemindersDataProvider } from './reminders-data-provider'
import { SidepanelLockButton } from './components/sidepanel/sidepanel-lock-button'
import { ViewChromeNav } from './components/sidepanel/view-chrome-nav'
import { SidepanelIntegrationsBanner } from './components/sidepanel/sidepanel-integrations-banner'
import { SidepanelPlanPaywall } from './components/sidepanel/sidepanel-plan-paywall'
import { useHonorSidepanelPaywallRequest } from './components/sidepanel/sidepanel-paywall-request'
import { SidepanelSignInGate } from './components/sidepanel/sidepanel-sign-in-gate'
import { SidepanelLegalGate } from './components/sidepanel/sidepanel-legal-gate'
import { SidepanelChatInput } from './components/sidepanel/sidepanel-chat-input'
import { SidepanelFloatingBar } from './components/sidepanel/nested/floating-bar'
import { SidepanelFooterSlot } from './components/sidepanel/nested/footer-slot'
import { SidepanelFooterOverlay } from './components/sidepanel/nested/footer-overlay'
import { useSidepanel, useSetSidepanelState } from '@renderer/overlay/atoms'
import { useQuickTourActive, useQuickTourStep, useFooterSlotClaim, useSetSidepanelViewAreaHeight } from './atoms'
import { useElementHeightObserver } from './hooks/use-element-height-observer'
import {
  useViewNavigation,
  useViewEntryStatePruning,
  useHomeNavigationHistory,
  useResetViews,
  useViewActions,
} from './view-atoms'
import { twMerge } from 'tailwind-merge'
import { View } from './components/view'
import { ViewRouter } from './view-router'
import { ViewSheetLayer } from './view-sheet-layer'
import { viewContextOf } from './view-definitions'
import { MEETING_VIEW_ID } from './components/meeting/meeting-view-id'
import { useSidepanelEventHandler } from './hooks/use-sidepanel-event-handler'
import { useStartChatEvent } from './hooks/use-start-chat-event'
import { useViewDraftPersistence } from './hooks/use-view-draft-persistence'
import { useToolbarDragSurface } from './hooks/use-toolbar-drag-surface'
import { useSidepanelHotkeyHandler } from './hooks/use-sidepanel-hotkey-handler'
import { useHistoryActivityInvalidation } from './hooks/use-history-activity'
import { useAuthStatus } from '@renderer/hooks/use-auth-status'
import { useIsOnboarded } from '@renderer/hooks/useIsOnboarded'
import { QuickTour } from './components/quicktour'
import { useQuickTourOverlayState } from './components/quicktour/use-quick-tour-overlay-state'
import { setOverlayInteractive } from '@highlight/ui'
import { isMac } from '@shared/utils/platform'
import { SidepanelFindInPage } from './components/sidepanel/sidepanel-find-in-page'
import { DayZeroTour } from './components/day-zero-tour'
import { MissionCoachMark } from './components/day-zero-tour/mission-coach-mark'
import { useCompleteTourStop } from './components/day-zero-tour/use-day-zero-tour'
import {
  SidebarSearchBar,
  SidebarSearchResults,
  useCanUseSidebarSearch,
  useIsSidebarSearching,
} from './components/sidebar-search'

export const SidepanelOverlay = memo(function SidepanelOverlay() {
  const [sidepanelState] = useSidepanel()
  const setSidepanelState = useSetSidepanelState()
  const quickTourActive = useQuickTourActive()
  const quickTourStep = useQuickTourStep()
  const { topView, topEntryKey, canGoBack: isViewOpen } = useViewNavigation()
  // Mounted here, not on the paywall strip: Home unmounts while a View is on
  // top, which is exactly when a gate's request needs the stack cleared.
  useHonorSidepanelPaywallRequest()
  const isChatViewOpen = topView.id === 'chat'
  const footerSlotClaim = useFooterSlotClaim()
  const closeActiveChat = useCloseActiveChat()
  const authStatus = useAuthStatus()
  const isOnboarded = useIsOnboarded()
  const resetViews = useResetViews()
  const { closeAllViews } = useViewActions()
  const canUseSidebarSearch = useCanUseSidebarSearch()
  const isSidebarSearching = useIsSidebarSearching() && canUseSidebarSearch
  // Settings missions hide the panel, so finishing one reopens it to celebrate.
  const completeTourStop = useCompleteTourStop()
  const handleMissionComplete = useCallback(
    (mission: string, skipped: boolean) => {
      completeTourStop(mission, skipped)
      setSidepanelState('open')
    },
    [completeTourStop, setSidepanelState],
  )

  // Wipe view history once auth is definitively signed out. The
  // auth:signout_completed event already does this for explicit sign-out, but
  // not every path there is guaranteed (session expiry races, state restored
  // on a signed-out cold start) — and a stale drill-in would dim the sign-in
  // gate (Body's isViewOpen opacity) and leave live back/forward chevrons
  // pointed at an unmounted ViewRouter.
  useEffect(() => {
    if (authStatus === 'signedOut') {
      resetViews()
    }
  }, [authStatus, resetViews])

  // Open on the sign-in gate. OverlayWindow skips blur-dismiss for this state.
  useEffect(() => {
    if (authStatus === 'signedOut' && isOnboarded) {
      setSidepanelState('open')
    }
  }, [authStatus, isOnboarded, setSidepanelState])

  const isQuickTourOpen = quickTourActive && sidepanelState === 'open'
  const { isQuickTourLocked, getQuickTourSectionClass } = useQuickTourOverlayState({
    isQuickTourOpen,
    quickTourStep,
  })

  // Handle sidepanel:openView events from other windows (e.g. base window artifact links)
  useSidepanelEventHandler()
  useStartChatEvent()
  useSidepanelHotkeyHandler()
  // Single mount point for history feed invalidation (see hook docs).
  useHistoryActivityInvalidation()
  // Per-history-entry state: save/restore unsent chat drafts, prune dead entries.
  useViewDraftPersistence()
  useViewEntryStatePruning()
  useHomeNavigationHistory()

  // Drag and drop handlers for file attachments
  const dragAndDrop = useDragAndDrop(sidepanelState === 'open')

  // Clear the active chat whenever no transcript surface is showing it, so
  // submissions from Home always start a new chat. (HIG-4293's race is handled
  // by the GlobalStore-backed mapping in the Chat View.) The Action View's
  // chat drawer (HIG-5977), the Output View's follow-up drawer, and the Meeting
  // View's chat drawer are the other claiming surfaces — their claims must be released here on dismissal or on
  // leaving the view, or the dismissed thread would swallow the next Home
  // submission as a follow-up. Safe on the first commit of a hard refresh
  // because `viewHistoryAtom` hydrates on first read: a persisted chat view is
  // already on top here, not a provisional Home whose clear would wipe the
  // linkage the restore is about to need.
  const isActionChatDrawerOpen = topView.id === 'action' && Boolean(viewContextOf(topView, 'action')?.chat)
  const isOutputChatDrawerOpen = topView.id === 'chatOutput' && Boolean(viewContextOf(topView, 'chatOutput')?.chat)
  const isMeetingChatDrawerOpen =
    topView.id === MEETING_VIEW_ID && Boolean(viewContextOf(topView, MEETING_VIEW_ID)?.chat)
  const isTranscriptSurfaceOpen =
    isChatViewOpen || isActionChatDrawerOpen || isOutputChatDrawerOpen || isMeetingChatDrawerOpen
  useEffect(() => {
    if (!isTranscriptSurfaceOpen) {
      closeActiveChat()
    }
  }, [isTranscriptSurfaceOpen, closeActiveChat])

  const viewAreaRef = useElementHeightObserver(useSetSidepanelViewAreaHeight())
  const handleToolbarPointerDown = useToolbarDragSurface()

  // Keep the overlay interactive while QuickTour is visible.
  useEffect(() => {
    if (quickTourActive && sidepanelState === 'open') {
      setOverlayInteractive(true)
      return () => {
        setOverlayInteractive(false)
      }
    }
  }, [quickTourActive, sidepanelState])

  return (
    <>
      <RemindersDataProvider>
        <Sidepanel>
          <Sidepanel.Content {...dragAndDrop} className={isQuickTourLocked ? 'pointer-events-none' : undefined}>
            <SidepanelFindInPage viewKey={topEntryKey} />

            <Sidepanel.Header>
              {/* No background of its own — the header is a static flex child,
                  so the unified surface's translucency stays uniform from the
                  top bar through the body. */}
              <div className={twMerge('flex w-full flex-col border-b', getQuickTourSectionClass('toolbar'))}>
                <div
                  className={twMerge(
                    'flex h-12 cursor-grab select-none items-center justify-between pl-3 active:cursor-grabbing',
                    isMac ? 'pr-2' : 'pr-0',
                  )}
                  data-tour-section="toolbar"
                  onPointerDown={handleToolbarPointerDown}
                >
                  {/* Window controls + pin are fixed chrome, so nav appearing on
                      drill-in grows the row instead of shoving controls over.
                      Mac traffic lights stay left; Windows caption buttons sit
                      after the toolbar on the right, matching native layout. */}
                  <div className="flex items-center gap-1 overflow-hidden">
                    {isMac && <Sidepanel.Trigger />}
                    <ViewChromeNav pin={<SidepanelLockButton />} />
                  </div>
                  <div className="flex h-full items-center gap-1">
                    <SidepanelToolbarSection />
                    {!isMac && <Sidepanel.Trigger />}
                  </div>
                </div>

                {authStatus === 'signedIn' && !isViewOpen && canUseSidebarSearch && <SidebarSearchBar />}

                <Sidepanel.Announcement />

                {authStatus === 'signedIn' && <SidepanelIntegrationsBanner />}
              </div>
            </Sidepanel.Header>

            <div ref={viewAreaRef} className="relative flex min-h-0 flex-1 flex-col">
              <Sidepanel.Body scrollLocked={isQuickTourLocked} isViewOpen={isViewOpen}>
                {/* Single-top invariant (ADR 0001): Home unmounts while a drill-in
                    View is on top — no hidden view trees rendering in the background. */}
                {authStatus === 'signedIn' && !isViewOpen && isSidebarSearching && <SidebarSearchResults />}
                {authStatus === 'signedIn' && !isViewOpen && !isSidebarSearching && (
                  <>
                    <SidepanelPlanPaywall />
                    <DayZeroTour className="mx-4 mb-0 mt-3" />
                    <SidepanelSections
                      getQuickTourSectionClass={getQuickTourSectionClass}
                      dragEnabled={!isQuickTourLocked}
                    />
                  </>
                )}
                {/* Post-onboarding signed-out gate. Ignores isViewOpen on purpose:
                    the reset effect above clears stale view history an effect-tick
                    after sign-out lands, and the gate must not wait on it. 'loading'
                    renders nothing so the gate can't flash before auth hydrates. */}
                {authStatus === 'signedOut' && isOnboarded && <SidepanelSignInGate />}
              </Sidepanel.Body>

              {authStatus === 'signedIn' && <ViewRouter />}
              {authStatus === 'signedIn' && <ViewSheetLayer />}
            </div>

            {/* File drag-and-drop for chat attachments */}
            <DragAndDrop isExpanded={sidepanelState === 'open'} />

            <Sidepanel.Footer data-tour-section="chat">
              <View.Glow />
              <SidepanelFloatingBar status={<Sidepanel.Toasts />} />
              {authStatus === 'signedIn' && (
                <SidepanelFooterSlot
                  height={footerSlotClaim?.height}
                  instant={footerSlotClaim?.instant}
                  overlay={<SidepanelFooterOverlay />}
                >
                  <SidepanelChatInput sidepanelState={sidepanelState} />
                </SidepanelFooterSlot>
              )}
            </Sidepanel.Footer>

            <SidepanelLegalGate />
          </Sidepanel.Content>
        </Sidepanel>
        {/* Keep fixed, non-portaled modals outside the panel's transformed,
            height-limited root so their scrims cover the full overlay. */}
        <ModalRenderer />
      </RemindersDataProvider>
      <QuickTour isSidepanelOpen={sidepanelState === 'open'} />
      {/* Outside the panel, which unmounts while hidden, so a settings mission still gets seen to finish. */}
      <MissionCoachMark
        surface="overlay"
        onMissionComplete={handleMissionComplete}
        onBackToTour={closeAllViews}
        panelOpen={sidepanelState === 'open'}
      />
    </>
  )
})
